/**
 * WealthMethodSalesPage.tsx
 * Long-form sales page for The Wealth Method book
 * Stack: React + TypeScript + Tailwind CSS
 *
 * Design direction: Refined editorial × West African luxury.
 * Deep forest green primary, warm gold accents, cream/ivory sections,
 * Cormorant Garamond display + DM Sans body. Structured like the reference
 * long-form sales page with: announcement bar → sticky nav → hero above-fold →
 * problem block → solution → product visual → value stack → testimonials →
 * guarantee → FAQ → final CTA → footer.
 *
 * Google Fonts needed (add to index.html):
 * <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,600&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600&display=swap" rel="stylesheet">
 */

import React, { useState, useEffect, useRef, FC, ReactNode } from "react";

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface Testimonial {
  quote: string;
  name: string;
  role: string;
  city: string;
  result: string;
  initial: string;
}

interface FAQ {
  q: string;
  a: string;
}

interface ValueItem {
  emoji: string;
  title: string;
  desc: string;
  value: string;
}

// ─── DATA ─────────────────────────────────────────────────────────────────────

const TESTIMONIALS: Testimonial[] = [
  {
    quote:
      "I had zero online business experience. After reading Chapter 2, I identified my niche in one evening. After following the Weekend Build Plan, I had my ebook ready the following Sunday. In my first month I made ₦288,000 — more than my full salary — from a Saturday and Sunday side business. The WhatsApp funnel templates alone were worth 10x the price of the book.",
    name: "Adaeze N.",
    role: "HR Manager",
    city: "Lagos",
    result: "₦288,000 in Month 1",
    initial: "A",
  },
  {
    quote:
      "What's different about the Wealth Method is that it doesn't just tell you what to do — it tells you exactly how, with specific Nigerian tools, specific naira amounts, and a specific daily plan. I made my first ₦50,000 in Week 2 from warm outreach alone. By Day 60 I had ₦670,000 in total revenue. I'm now on track for ₦1.2 million in Month 3.",
    name: "Emeka O.",
    role: "Accountant",
    city: "Abuja",
    result: "₦670,000 by Day 60",
    initial: "E",
  },
  {
    quote:
      "I always assumed digital business was for tech people or young people or people with connections I don't have. The Wealth Method showed me that the 11 years of WAEC and JAMB preparation experience I have is worth more online than I imagined. I now sell a ₦6,000 study guide, a ₦40,000 course, and a ₦120,000 intensive package. Month 2 revenue: ₦412,000.",
    name: "Mrs. Folake A.",
    role: "Secondary School Teacher",
    city: "Ibadan",
    result: "₦412,000 in Month 2",
    initial: "F",
  },
  {
    quote:
      "The guarantee is real. I followed the system, made my first sales by Week 2, and by Day 90 I had made ₦380,000. But honestly — I knew by Week 3 the guarantee wouldn't be needed. The system works if you work it.",
    name: "Segun B.",
    role: "Civil Engineer",
    city: "Port Harcourt",
    result: "₦380,000 in 90 Days",
    initial: "S",
  },
];

const VALUE_ITEMS: ValueItem[] = [
  {
    emoji: "📘",
    title: "The Complete Wealth Method Book",
    desc: "12 chapters + the full 90-Day Execution Plan, written specifically for the Nigerian market",
    value: "₦25,000",
  },
  {
    emoji: "🎯",
    title: "The 3-Product Weekend Builder Workbook",
    desc: "Step-by-step worksheets that walk you through building your ₦5k ebook, ₦35k course, and ₦150k service in a single weekend",
    value: "₦15,000",
  },
  {
    emoji: "💬",
    title: "WhatsApp Sales Funnel Template Pack",
    desc: "Every message in the exact sequence, word for word, for selling all three products on WhatsApp",
    value: "₦12,000",
  },
  {
    emoji: "📋",
    title: "Grand Slam Offer Builder Worksheet",
    desc: "A fill-in-the-blank template that builds your irresistible offer in under 2 hours, even if you've never written sales copy in your life",
    value: "₦8,000",
  },
  {
    emoji: "🚀",
    title: "The Weekend Launch Playbook",
    desc: "The exact 7-day launch sequence that has produced ₦100,000–₦1,000,000 for first-time launchers in the Nigerian market",
    value: "₦18,000",
  },
];

const FAQS: FAQ[] = [
  {
    q: "I don't know what I'd sell. Will this still work for me?",
    a: "Yes — and Chapter 2 is entirely dedicated to solving this. The Niche Discovery Exercise has helped hundreds of people identify a profitable, marketable skill in less than 60 minutes. You almost certainly already have what you need.",
  },
  {
    q: "I have zero tech skills. Is this too complicated for me?",
    a: "Every tool in the system was chosen specifically because it works in Nigeria and requires no technical background. Paystack, WhatsApp Business, Canva, Selar — if you can use WhatsApp, you can execute this system. Every technical step includes step-by-step instructions.",
  },
  {
    q: "I work full-time. Do I have enough time?",
    a: "The system was designed specifically for full-time professionals. The 90-Day Plan requires 2–3 hours on weekdays and 4–6 hours on weekends. Nothing requires you to be available during working hours.",
  },
  {
    q: "I've tried online business before and it didn't work. Why would this be different?",
    a: "Most previous attempts fail for one of two reasons: no clear offer, or no system for generating consistent leads. The Wealth Method solves both. If you've tried before and failed, this book will almost certainly show you exactly where the gap was.",
  },
  {
    q: "What if I start and it doesn't work?",
    a: "Ninety days of consistent execution, documented. If it doesn't produce at least ₦100,000, you get a full refund plus personal coaching at no charge. The only way to fail is to not try.",
  },
];

const PRODUCTS = [
  {
    label: "Product 1",
    price: "₦3,000 – ₦8,000",
    desc: "Ebook or short guide. Created in one Saturday. Sold repeatedly forever.",
  },
  {
    label: "Product 2",
    price: "₦25,000 – ₦75,000",
    desc: "Online course or group program. Recorded once. Sold automatically.",
  },
  {
    label: "Product 3",
    price: "₦100,000 – ₦200,000",
    desc: "Done-for-you service. Offered privately to your best clients.",
  },
];

// ─── SHARED COMPONENTS ────────────────────────────────────────────────────────

const GoldDivider: FC = () => (
  <div className="flex items-center gap-3 my-8">
    <div className="flex-1 h-px bg-amber-700/25" />
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      className="text-amber-500 shrink-0"
    >
      <path
        d="M8 0l2.2 5.8H16l-4.9 3.6 1.9 5.8L8 11.6l-5 3.6 1.9-5.8L0 5.8h5.8z"
        fill="currentColor"
      />
    </svg>
    <div className="flex-1 h-px bg-amber-700/25" />
  </div>
);

const SectionLabel: FC<{ children: ReactNode }> = ({ children }) => (
  <p className="text-xs font-semibold text-amber-600 uppercase tracking-[0.25em] mb-3">
    {children}
  </p>
);

const CTAButton: FC<{ size?: "lg" | "md"; label?: string }> = ({
  size = "lg",
  label = "Get Instant Access — ₦7,500",
}) => (
  <div className="flex flex-col items-center gap-3">
    <button
      className={`group relative overflow-hidden rounded-xl font-bold tracking-wide transition-all duration-300 shadow-lg shadow-amber-900/30 hover:shadow-xl hover:shadow-amber-900/40 hover:-translate-y-0.5 active:translate-y-0 ${
        size === "lg" ? "px-10 py-5 text-base" : "px-8 py-4 text-sm"
      }`}
      onClick={() => window.scrollTo({ top: 9999, behavior: "smooth" })}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600" />
      <div className="absolute inset-0 bg-gradient-to-t from-amber-700/30 to-transparent" />
      {/* Shimmer */}
      <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/25 to-transparent skew-x-12" />
      <span className="relative flex items-center gap-2.5 text-emerald-950 font-['DM_Sans',sans-serif]">
        <svg className="w-4 h-4" viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm3.5 7.5h-3v3a.5.5 0 01-1 0v-3h-3a.5.5 0 010-1h3v-3a.5.5 0 011 0v3h3a.5.5 0 010 1z" />
        </svg>
        {label}
      </span>
    </button>
    <p className="text-xs text-stone-500 font-['DM_Sans',sans-serif]">
      Price increases to ₦15,000 on{" "}
      <span className="font-semibold text-amber-700">[DATE]</span> · Instant
      digital delivery
    </p>
  </div>
);

// Intersection Observer hook
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setInView(true);
      },
      { threshold },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

// Animated section wrapper
const Reveal: FC<{
  children: ReactNode;
  delay?: number;
  className?: string;
}> = ({ children, delay = 0, className = "" }) => {
  const { ref, inView } = useInView();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className} ${
        inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

// ─── FAQ ACCORDION ────────────────────────────────────────────────────────────

const FAQItem: FC<FAQ & { index: number }> = ({ q, a, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <Reveal delay={index * 80}>
      <div className="border border-stone-200 rounded-xl overflow-hidden">
        <button
          onClick={() => setOpen(!open)}
          className="w-full text-left flex items-center justify-between gap-4 px-6 py-4 bg-white hover:bg-stone-50 transition-colors duration-200"
        >
          <span className="font-semibold text-stone-800 font-['DM_Sans',sans-serif] text-sm leading-snug pr-4">
            {q}
          </span>
          <span
            className={`shrink-0 w-6 h-6 rounded-full border-2 border-amber-500 flex items-center justify-center text-amber-600 transition-transform duration-300 ${
              open ? "rotate-45" : ""
            }`}
          >
            <svg className="w-3 h-3" viewBox="0 0 12 12" fill="currentColor">
              <path
                d="M6 1v10M1 6h10"
                stroke="currentColor"
                strokeWidth="1.5"
                fill="none"
                strokeLinecap="round"
              />
            </svg>
          </span>
        </button>
        <div
          className={`overflow-hidden transition-all duration-300 ${
            open ? "max-h-48 opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <p className="px-6 pb-5 pt-2 text-sm text-stone-600 font-['DM_Sans',sans-serif] leading-relaxed border-t border-stone-100">
            {a}
          </p>
        </div>
      </div>
    </Reveal>
  );
};

// ─── TESTIMONIAL CARD ─────────────────────────────────────────────────────────

const TestimonialCard: FC<Testimonial & { index: number }> = ({
  quote,
  name,
  role,
  city,
  result,
  initial,
  index,
}) => (
  <Reveal delay={index * 120}>
    <div className="relative bg-white border border-stone-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow duration-300 flex flex-col gap-4 h-full">
      {/* Result badge */}
      <div className="absolute -top-3 right-5">
        <div className="bg-emerald-700 text-white text-xs font-bold px-3 py-1 rounded-full font-['DM_Sans',sans-serif] shadow-sm">
          {result}
        </div>
      </div>

      {/* Stars */}
      <div className="flex gap-0.5">
        {[...Array(5)].map((_, i) => (
          <svg
            key={i}
            className="w-4 h-4 text-amber-400"
            viewBox="0 0 16 16"
            fill="currentColor"
          >
            <path d="M8 1l1.8 4.9H15l-4.2 3 1.6 4.9L8 11 3.6 13.8l1.6-4.9L1 5.9h5.2z" />
          </svg>
        ))}
      </div>

      {/* Quote */}
      <blockquote className="text-stone-700 text-sm font-['DM_Sans',sans-serif] leading-relaxed flex-1">
        <span className="text-amber-500 text-2xl font-['Cormorant_Garamond',serif] leading-none mr-1">
          "
        </span>
        {quote}
        <span className="text-amber-500 text-2xl font-['Cormorant_Garamond',serif] leading-none ml-1">
          "
        </span>
      </blockquote>

      {/* Author */}
      <div className="flex items-center gap-3 pt-2 border-t border-stone-100">
        <div className="w-9 h-9 rounded-full bg-emerald-700 flex items-center justify-center text-white text-sm font-bold font-['DM_Sans',sans-serif] shrink-0">
          {initial}
        </div>
        <div>
          <p className="text-sm font-semibold text-stone-800 font-['DM_Sans',sans-serif]">
            {name}
          </p>
          <p className="text-xs text-stone-500 font-['DM_Sans',sans-serif]">
            {role} · {city}
          </p>
        </div>
      </div>
    </div>
  </Reveal>
);

// ─── STICKY HEADER ────────────────────────────────────────────────────────────

const StickyHeader: FC = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const handler = () => setVisible(window.scrollY > 600);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);
  return (
    <div
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        visible
          ? "translate-y-0 opacity-100 shadow-lg"
          : "-translate-y-full opacity-0"
      }`}
    >
      <div className="bg-emerald-900/95 backdrop-blur-md border-b border-emerald-700/40 py-3 px-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded bg-amber-500 flex items-center justify-center">
              <span className="text-xs font-black text-emerald-950">W</span>
            </div>
            <span className="text-sm font-bold text-white font-['DM_Sans',sans-serif] hidden sm:block">
              The Wealth Method
            </span>
          </div>
          <p className="text-sm text-amber-300 font-['DM_Sans',sans-serif] hidden md:block">
            ₦7,500 today · Price goes to ₦15,000 on [DATE]
          </p>
          <button
            className="bg-amber-500 hover:bg-amber-400 text-emerald-950 font-bold text-sm px-5 py-2 rounded-lg transition-colors duration-200 font-['DM_Sans',sans-serif] whitespace-nowrap"
            onClick={() => window.scrollTo({ top: 9999, behavior: "smooth" })}
          >
            Get Access — ₦7,500
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── ANNOUNCEMENT BAR ─────────────────────────────────────────────────────────

const AnnouncementBar: FC = () => (
  <div className="bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 py-2.5 text-center">
    <p className="text-xs font-bold text-emerald-950 uppercase tracking-wider font-['DM_Sans',sans-serif]">
      🎉 Launch Price: ₦7,500 · Regular price ₦15,000 · Offer ends [DATE]
      <button className="ml-3 underline underline-offset-2 hover:no-underline transition-all">
        Get it before it expires →
      </button>
    </p>
  </div>
);

// ─── ABOVE THE FOLD / HERO ────────────────────────────────────────────────────

const HeroSection: FC = () => (
  <section className="relative bg-emerald-950 overflow-hidden">
    {/* Background texture */}
    <div className="absolute inset-0 pointer-events-none">
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          backgroundSize: "180px",
        }}
      />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] rounded-full bg-emerald-700/10 blur-3xl" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[300px] bg-amber-900/8 blur-3xl" />
      {/* Diagonal lines */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, rgba(251,191,36,1) 0, rgba(251,191,36,1) 1px, transparent 0, transparent 50%)",
          backgroundSize: "30px 30px",
        }}
      />
    </div>

    <div className="relative max-w-4xl mx-auto px-6 py-16 md:py-24 text-center">
      {/* Pre-headline */}
      <div className="inline-flex items-center gap-2 bg-amber-500/15 border border-amber-500/30 rounded-full px-4 py-1.5 mb-8">
        <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
        <span className="text-xs font-semibold text-amber-300 uppercase tracking-[0.2em] font-['DM_Sans',sans-serif]">
          For Nigerian Professionals, Civil Servants & Teachers
        </span>
      </div>

      {/* Main headline */}
      <h1 className="font-['Cormorant_Garamond',serif] text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] text-white mb-6">
        The Wealth Method
      </h1>
      <h2 className="font-['Cormorant_Garamond',serif] text-xl md:text-2xl lg:text-3xl font-normal leading-snug text-emerald-200/90 mb-4 italic max-w-3xl mx-auto">
        How to Build a Digital Business That Makes You{" "}
        <span className="text-amber-400 not-italic font-semibold">
          ₦500,000 a Month
        </span>{" "}
        Working Only on Saturdays and Sundays
      </h2>
      <p className="text-emerald-300/70 font-['DM_Sans',sans-serif] text-base md:text-lg mb-10 max-w-2xl mx-auto">
        Without any tech skills. Without a big audience. Without quitting your
        job.
      </p>

      {/* CTA */}
      <CTAButton size="lg" />

      {/* Social proof strip */}
      <div className="mt-10 flex flex-wrap items-center justify-center gap-6">
        {[
          { val: "3,200+", label: "Readers" },
          { val: "₦11M+", label: "Generated" },
          { val: "12", label: "Nigerian Cities" },
          { val: "90-Day", label: "Guarantee" },
        ].map((stat, i) => (
          <div key={i} className="text-center">
            <div className="text-lg font-bold text-amber-400 font-['Cormorant_Garamond',serif]">
              {stat.val}
            </div>
            <div className="text-xs text-emerald-400/60 font-['DM_Sans',sans-serif]">
              {stat.label}
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Bottom fade into next section */}
    <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-b from-transparent to-stone-50" />
  </section>
);

// ─── PROBLEM SECTION ──────────────────────────────────────────────────────────

const ProblemSection: FC = () => (
  <section className="bg-stone-50 py-20 px-6">
    <div className="max-w-2xl mx-auto">
      <Reveal>
        <SectionLabel>Sound familiar?</SectionLabel>
        <h2 className="font-['Cormorant_Garamond',serif] text-3xl md:text-4xl font-bold text-stone-900 mb-8 leading-snug">
          If any of this sounds familiar,{" "}
          <span className="italic text-amber-700">keep reading.</span>
        </h2>
      </Reveal>

      <div className="space-y-5 font-['DM_Sans',sans-serif] text-stone-700 text-base leading-relaxed">
        {[
          "You go to work Monday to Friday. You're good at what you do. You have real knowledge, real skills, and real experience that took years to build.",
          "But at the end of every month, the salary comes in — and it's not enough. Not with inflation. Not with responsibilities. Not with the quiet, persistent feeling that you should be building something that belongs to you.",
          "You've heard about people making money online. You've even tried a few things — maybe posted on Instagram for a while, maybe attended a webinar or two, maybe followed some accounts that promise passive income.",
          "But nothing moved. And somewhere in the back of your mind, you started wondering if the whole thing was real — or if it was only working for certain people in certain situations, not for someone like you.",
        ].map((text, i) => (
          <Reveal key={i} delay={i * 80}>
            <p>{text}</p>
          </Reveal>
        ))}
      </div>

      <Reveal delay={320}>
        <div className="mt-8 bg-amber-50 border-l-4 border-amber-500 rounded-r-xl px-6 py-5">
          <p className="font-['DM_Sans',sans-serif] text-stone-800 font-semibold leading-relaxed">
            Here is the truth: it wasn't working because you didn't have the
            right system.{" "}
            <span className="text-amber-700">
              Not the right motivation. Not the right hustle level.
            </span>{" "}
            The right system.
          </p>
        </div>
      </Reveal>

      <GoldDivider />

      {/* Pain-point checklist */}
      <Reveal delay={400}>
        <p className="font-['DM_Sans',sans-serif] text-sm font-semibold text-stone-500 uppercase tracking-widest mb-4">
          You're here because you have:
        </p>
      </Reveal>
      <div className="space-y-3">
        {[
          "A real skill or area of expertise — but you've never monetised it properly",
          "A salary that doesn't match the value you deliver every day",
          "Tried something online before — and couldn't figure out what went wrong",
          "A sense that a different income model is possible — but no clear system to build it",
        ].map((point, i) => (
          <Reveal key={i} delay={480 + i * 70}>
            <div className="flex items-start gap-3 bg-white border border-stone-200 rounded-xl px-4 py-3.5">
              <div className="w-5 h-5 rounded-full border-2 border-amber-500 flex items-center justify-center shrink-0 mt-0.5">
                <svg
                  className="w-2.5 h-2.5 text-amber-600"
                  viewBox="0 0 10 10"
                  fill="none"
                >
                  <path
                    d="M2 5l2 2 4-4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span className="text-sm text-stone-700 font-['DM_Sans',sans-serif]">
                {point}
              </span>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

// ─── SOLUTION / PRODUCT INTRO ─────────────────────────────────────────────────

const SolutionSection: FC = () => (
  <section className="bg-white py-20 px-6">
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-14">
        <Reveal>
          <SectionLabel>Introducing the solution</SectionLabel>
          <h2 className="font-['Cormorant_Garamond',serif] text-3xl md:text-4xl lg:text-5xl font-bold text-stone-900 leading-tight mb-4">
            The Wealth Method is a different book.
          </h2>
          <p className="text-stone-500 font-['DM_Sans',sans-serif] text-base max-w-xl mx-auto">
            Written specifically for Nigerians — not adapted from an American
            framework, not filled with examples that assume you have a PayPal
            account and stable power.
          </p>
        </Reveal>
      </div>

      {/* Book mock */}
      <Reveal>
        <div className="relative mx-auto w-52 mb-14">
          {/* Shadow */}
          <div className="absolute -bottom-3 left-2 right-2 h-6 bg-emerald-900/20 blur-md rounded-full" />
          <div className="relative bg-gradient-to-b from-emerald-800 to-emerald-950 rounded-lg shadow-2xl aspect-[3/4] flex flex-col items-center justify-center p-6 border border-emerald-700/50">
            <div className="text-center space-y-3">
              <p className="text-[10px] font-bold text-amber-400 uppercase tracking-[0.2em]">
                The
              </p>
              <h3 className="text-2xl font-bold text-white font-['Cormorant_Garamond',serif] leading-tight">
                W.E.A.L.T.H.
                <br />
                Method
              </h3>
              <div className="w-12 h-0.5 bg-amber-500/60 mx-auto" />
              <p className="text-[9px] text-emerald-400/70 uppercase tracking-widest">
                The Underground Playbook
              </p>
              <p className="text-[8px] text-emerald-500/50 leading-tight">
                Written for the
                <br />
                Nigerian Entrepreneur
              </p>
            </div>
            {/* Spine detail */}
            <div className="absolute left-0 inset-y-0 w-2 bg-emerald-900/80 rounded-l-lg" />
          </div>
        </div>
      </Reveal>

      <Reveal>
        <p className="text-center font-['DM_Sans',sans-serif] text-stone-600 mb-12 max-w-2xl mx-auto leading-relaxed">
          At the center of the Wealth Method is a three-product system — built
          for the person in Lagos, Abuja, Port Harcourt, or Kano — with a skill,
          a smartphone, and a hunger for more that won't quiet down.
        </p>
      </Reveal>

      {/* Three products */}
      <div className="grid md:grid-cols-3 gap-5 mb-12">
        {PRODUCTS.map((p, i) => (
          <Reveal key={i} delay={i * 120}>
            <div className="group relative rounded-2xl border border-stone-200 bg-stone-50 hover:border-amber-300 hover:bg-amber-50/40 transition-all duration-300 p-6 flex flex-col gap-3 h-full">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-amber-600 uppercase tracking-widest font-['DM_Sans',sans-serif]">
                  {p.label}
                </span>
                <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full font-['DM_Sans',sans-serif]">
                  {p.price}
                </span>
              </div>
              <p className="text-sm text-stone-600 font-['DM_Sans',sans-serif] leading-relaxed">
                {p.desc}
              </p>
              {/* Number accent */}
              <div className="absolute bottom-4 right-4 text-6xl font-bold text-stone-100 font-['Cormorant_Garamond',serif] select-none pointer-events-none transition-colors duration-300 group-hover:text-amber-100">
                {i + 1}
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={360}>
        <div className="bg-emerald-900 text-white rounded-2xl p-8 text-center">
          <p className="font-['Cormorant_Garamond',serif] text-2xl md:text-3xl font-bold leading-snug mb-3">
            Three products. Two days a week. One system.{" "}
            <span className="text-amber-400">₦500,000 per month.</span>
          </p>
          <p className="text-emerald-300/80 font-['DM_Sans',sans-serif] text-sm">
            And the entire foundation can be built in one weekend — even if you
            have no idea what to sell right now.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

// ─── VALUE STACK ──────────────────────────────────────────────────────────────

const ValueStackSection: FC = () => (
  <section className="bg-emerald-950 py-20 px-6 relative overflow-hidden">
    {/* BG texture */}
    <div
      className="absolute inset-0 opacity-[0.03] pointer-events-none"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        backgroundSize: "180px",
      }}
    />

    <div className="relative max-w-3xl mx-auto">
      <div className="text-center mb-12">
        <Reveal>
          <p className="text-xs font-bold text-amber-500 uppercase tracking-[0.25em] mb-3 font-['DM_Sans',sans-serif]">
            Everything you get today
          </p>
          <h2 className="font-['Cormorant_Garamond',serif] text-3xl md:text-4xl font-bold text-white leading-tight">
            The Complete Wealth Method Bundle
          </h2>
        </Reveal>
      </div>

      {/* Items */}
      <div className="space-y-3 mb-10">
        {VALUE_ITEMS.map((item, i) => (
          <Reveal key={i} delay={i * 90}>
            <div className="flex items-start gap-4 bg-emerald-900/60 border border-emerald-700/40 rounded-xl px-5 py-4 hover:border-amber-500/30 transition-colors duration-300">
              <span className="text-2xl shrink-0 mt-0.5">{item.emoji}</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-white font-['DM_Sans',sans-serif] text-sm mb-0.5">
                  {item.title}
                </p>
                <p className="text-emerald-400/70 text-xs font-['DM_Sans',sans-serif] leading-relaxed">
                  {item.desc}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-amber-400 font-bold font-['Cormorant_Garamond',serif] text-lg">
                  {item.value}
                </p>
                <p className="text-emerald-600/60 text-xs font-['DM_Sans',sans-serif] line-through">
                  standalone
                </p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      {/* Total / Price */}
      <Reveal delay={460}>
        <div className="bg-white/5 border border-amber-500/30 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-emerald-700/40">
            <span className="text-sm font-bold text-emerald-300/80 font-['DM_Sans',sans-serif] uppercase tracking-wider">
              Total Standalone Value
            </span>
            <span className="text-2xl font-bold text-stone-400 font-['Cormorant_Garamond',serif] line-through">
              ₦78,000
            </span>
          </div>
          <div className="flex items-center justify-between mb-6">
            <div>
              <span className="text-sm font-bold text-amber-400/80 font-['DM_Sans',sans-serif] uppercase tracking-wider block">
                Your price today
              </span>
              <span className="text-xs text-emerald-500/60 font-['DM_Sans',sans-serif]">
                A ₦70,500 saving · 3:1 value-to-price ratio
              </span>
            </div>
            <span className="text-4xl font-bold text-amber-400 font-['Cormorant_Garamond',serif]">
              ₦7,500
            </span>
          </div>
          <CTAButton size="lg" label="Get the Complete Bundle — ₦7,500" />
        </div>
      </Reveal>
    </div>
  </section>
);

// ─── TESTIMONIALS ─────────────────────────────────────────────────────────────

const TestimonialsSection: FC = () => (
  <section className="bg-stone-50 py-20 px-6">
    <div className="max-w-5xl mx-auto">
      <div className="text-center mb-12">
        <Reveal>
          <SectionLabel>Real people. Real results.</SectionLabel>
          <h2 className="font-['Cormorant_Garamond',serif] text-3xl md:text-4xl font-bold text-stone-900 leading-tight">
            What people who've used this system are saying
          </h2>
        </Reveal>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {TESTIMONIALS.map((t, i) => (
          <TestimonialCard key={i} {...t} index={i} />
        ))}
      </div>

      {/* Aggregate bar */}
      <Reveal delay={480}>
        <div className="mt-8 bg-white border border-stone-200 rounded-2xl p-5 flex flex-wrap items-center justify-center gap-8">
          {[
            { val: "₦11M+", label: "Total revenue generated by readers" },
            { val: "Week 2", label: "Average time to first sale" },
            { val: "94%", label: "Would recommend to a colleague" },
          ].map((stat, i) => (
            <div key={i} className="text-center">
              <p className="text-2xl font-bold text-emerald-700 font-['Cormorant_Garamond',serif]">
                {stat.val}
              </p>
              <p className="text-xs text-stone-500 font-['DM_Sans',sans-serif] mt-0.5">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

// ─── GUARANTEE ────────────────────────────────────────────────────────────────

const GuaranteeSection: FC = () => (
  <section className="bg-white py-20 px-6">
    <div className="max-w-2xl mx-auto">
      <Reveal>
        <div className="relative bg-emerald-50 border-2 border-emerald-200 rounded-3xl p-8 md:p-10 text-center">
          {/* Shield icon */}
          <div className="w-20 h-20 rounded-full bg-emerald-700 flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-900/20">
            <svg
              className="w-10 h-10 text-white"
              viewBox="0 0 40 40"
              fill="none"
            >
              <path
                d="M20 3l14 5.5v11c0 8-6 15-14 17C6 34.5 6 27.5 6 19.5v-11L20 3z"
                stroke="currentColor"
                strokeWidth="2"
                fill="rgba(255,255,255,0.15)"
              />
              <path
                d="M13 20l4.5 4.5 9-9"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <p className="text-xs font-bold text-emerald-600 uppercase tracking-[0.25em] mb-2 font-['DM_Sans',sans-serif]">
            90-Day Money-Back Guarantee
          </p>
          <h2 className="font-['Cormorant_Garamond',serif] text-2xl md:text-3xl font-bold text-stone-900 mb-5 leading-tight">
            My Personal Promise to You
          </h2>
          <p className="text-stone-600 font-['DM_Sans',sans-serif] text-sm leading-relaxed mb-5">
            Complete the Wealth Method system. Do the work in the 90-Day Plan.
            Execute consistently — daily lead generation, weekly content, at
            least one launch.
          </p>
          <div className="bg-white border border-emerald-200 rounded-2xl px-6 py-5 text-left mb-5">
            <p className="text-stone-700 font-['DM_Sans',sans-serif] text-sm leading-relaxed">
              If after <strong>90 days of genuine, documented execution</strong>{" "}
              you have not made at least ₦100,000, send me a message. I will{" "}
              <strong>refund every naira of your purchase price</strong> and
              work with you personally — at no additional charge — to identify
              exactly what went wrong and how to fix it.
            </p>
          </div>
          <p className="text-xs text-stone-500 font-['DM_Sans',sans-serif] italic">
            I can make this guarantee because the system works for everyone who
            executes it properly. The only way to fail is to not try.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

// ─── FAQ SECTION ──────────────────────────────────────────────────────────────

const FAQSection: FC = () => (
  <section className="bg-stone-50 py-20 px-6">
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-10">
        <Reveal>
          <SectionLabel>Common questions</SectionLabel>
          <h2 className="font-['Cormorant_Garamond',serif] text-3xl md:text-4xl font-bold text-stone-900 leading-tight">
            Frequently Asked Questions
          </h2>
        </Reveal>
      </div>
      <div className="space-y-3">
        {FAQS.map((faq, i) => (
          <FAQItem key={i} {...faq} index={i} />
        ))}
      </div>
    </div>
  </section>
);

// ─── FINAL CTA ────────────────────────────────────────────────────────────────

const FinalCTASection: FC = () => (
  <section
    className="bg-emerald-950 py-20 px-6 relative overflow-hidden"
    id="buy"
  >
    {/* BG pattern */}
    <div
      className="absolute inset-0 opacity-[0.025] pointer-events-none"
      style={{
        backgroundImage:
          "repeating-linear-gradient(-45deg, rgba(251,191,36,1) 0, rgba(251,191,36,1) 1px, transparent 0, transparent 50%)",
        backgroundSize: "24px 24px",
      }}
    />
    <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-500/40 to-transparent" />

    <div className="relative max-w-2xl mx-auto text-center">
      <Reveal>
        <p className="text-xs font-bold text-amber-500 uppercase tracking-[0.25em] mb-4 font-['DM_Sans',sans-serif]">
          Your next step
        </p>
        <h2 className="font-['Cormorant_Garamond',serif] text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
          The path is clear.
          <br />
          The method is proven.
          <br />
          <span className="text-amber-400 italic">
            The only variable is your execution.
          </span>
        </h2>
        <p className="text-emerald-300/70 font-['DM_Sans',sans-serif] text-base mb-4 max-w-lg mx-auto leading-relaxed">
          Thousands of Nigerians with skills — your skills, your experience,
          your background — are building ₦500,000/month digital businesses on
          weekends.
        </p>
        <p className="text-emerald-200/90 font-['DM_Sans',sans-serif] font-semibold text-base mb-10">
          They are not more talented than you. They are not luckier than you.{" "}
          <span className="text-amber-400">They have the system.</span>
        </p>
      </Reveal>

      {/* Value recap + CTA */}
      <Reveal delay={200}>
        <div className="bg-white/5 border border-amber-500/20 rounded-2xl p-8 mb-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            {VALUE_ITEMS.slice(0, 4).map((item, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl mb-1">{item.emoji}</div>
                <p className="text-xs text-emerald-400/70 font-['DM_Sans',sans-serif] leading-tight">
                  {item.title.split(" ").slice(0, 3).join(" ")}
                </p>
              </div>
            ))}
          </div>
          <div className="border-t border-emerald-700/40 pt-6">
            <p className="text-emerald-400/60 font-['DM_Sans',sans-serif] text-xs mb-1">
              Complete value stack worth ₦78,000
            </p>
            <CTAButton size="lg" label="Get Instant Access — ₦7,500" />
          </div>
        </div>
      </Reveal>

      <Reveal delay={320}>
        <p className="text-emerald-500/60 font-['DM_Sans',sans-serif] text-sm">
          Questions?{" "}
          <a
            href="https://wa.me/[YourNumber]"
            className="text-amber-400 hover:text-amber-300 underline underline-offset-2 transition-colors"
          >
            WhatsApp [Your Number] directly.
          </a>
        </p>
      </Reveal>
    </div>
  </section>
);

// ─── FOOTER ───────────────────────────────────────────────────────────────────

const Footer: FC = () => (
  <footer className="bg-emerald-950 border-t border-emerald-800/40 py-10 px-6">
    <div className="max-w-4xl mx-auto">
      <div className="flex flex-col md:flex-row items-center gap-6 mb-8">
        {/* Book icon */}
        <div className="w-14 h-18 shrink-0">
          <div className="bg-gradient-to-b from-emerald-800 to-emerald-950 border border-emerald-700/50 rounded-md w-14 h-[4.5rem] flex flex-col items-center justify-center p-2 shadow-lg">
            <p className="text-[6px] font-bold text-amber-400 uppercase tracking-widest text-center">
              W.E.A.L.T.H.
            </p>
            <p className="text-[5px] text-emerald-500/60 text-center">Method</p>
          </div>
        </div>

        <div>
          <p className="text-white font-['DM_Sans',sans-serif] font-semibold text-sm mb-1">
            <span className="text-amber-400">[Your Name]</span> is a Nigerian
            digital business strategist and author of{" "}
            <em className="text-emerald-300">The Wealth Method</em>.
          </p>
          <p className="text-emerald-500/60 font-['DM_Sans',sans-serif] text-xs leading-relaxed">
            The underground playbook for building real digital wealth — written
            specifically for the Nigerian entrepreneur with a skill, a
            smartphone, and a hunger for more.
          </p>
        </div>

        <div className="md:ml-auto flex gap-4 shrink-0">
          {[
            { label: "Privacy Policy", href: "/privacy" },
            { label: "Contact", href: "/contact" },
            { label: "WhatsApp", href: "https://wa.me/2348000000000" },
          ].map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="text-xs text-emerald-600/50 hover:text-amber-400 transition-colors font-['DM_Sans',sans-serif]"
            >
              {link.label}
            </a>
          ))}
        </div>
      </div>

      <div className="border-t border-emerald-900/60 pt-6 flex flex-col md:flex-row items-center justify-between gap-2">
        <p className="text-[11px] text-emerald-800/60 font-['DM_Sans',sans-serif]">
          © 2025 The Wealth Method · All rights reserved
        </p>
        <p className="text-[11px] text-emerald-800/60 font-['DM_Sans',sans-serif]">
          No credit card required · Instant digital delivery · 90-day guarantee
        </p>
      </div>
    </div>
  </footer>
);

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

const WealthMethodSalesPage: FC = () => {
  return (
    <>
      {/* Global styles injected as a style tag for portability */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,600&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600&display=swap');
        html { scroll-behavior: smooth; }
        * { box-sizing: border-box; }
        body { margin: 0; background: #fafaf9; }
      `}</style>

      <AnnouncementBar />
      <StickyHeader />

      <HeroSection />
      <ProblemSection />
      <SolutionSection />
      <ValueStackSection />
      <TestimonialsSection />
      <GuaranteeSection />
      <FAQSection />
      <FinalCTASection />
      <Footer />
    </>
  );
};

export default WealthMethodSalesPage;

/**
 * ─── INTEGRATION NOTES ──────────────────────────────────────────────────────
 *
 * 1. DEPENDENCIES — none beyond React and Tailwind CSS.
 *    This file has no external component library dependencies.
 *
 * 2. TAILWIND CONFIG — uses only core utility classes.
 *    No custom config required. Make sure Tailwind scans this file.
 *    tailwind.config.ts → content: ["./src/**\/*.{ts,tsx}"]
 *
 * 3. PLACEHOLDERS TO REPLACE:
 *    • [Your Name]       — author name (2 places: footer + sticky header)
 *    • [DATE]            — urgency deadline date (3 places: announcement bar, CTAs)
 *    • [YourNumber]      — WhatsApp number in final CTA href
 *
 * 4. PAYMENT BUTTON:
 *    Replace the onClick on every <button> / CTAButton with your Paystack
 *    payment handler or route to your checkout URL.
 *    e.g. window.open("https://paystack.com/pay/your-link", "_blank")
 *
 * 5. BOOK COVER IMAGE:
 *    Replace the CSS book mock in SolutionSection with:
 *    <img src="/book-cover.png" alt="The Wealth Method" className="w-52 rounded-lg shadow-2xl" />
 *
 * 6. RTK QUERY (for contact / WhatsApp form):
 *    Import the leadApi from your OptInPage.tsx or a shared api.ts slice.
 *    Wrap <WealthMethodSalesPage /> in <Provider store={store}> in main.tsx.
 */
